import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux"
import { Box, Typography } from "@mui/material";
import SearchIcon from '@mui/icons-material/Search';
import { useQuery } from "@apollo/client";

import MainContainer from "../../components/Container/MainContainer";
import BlockContainer from "../../components/Container/BlockContainer";
import BaseIconInput from "../../components/BaseIconInput";
import PatientTable from "../../components/PatientTable";
import PatientList from "../../components/PatientList";
import PatientListOrderKit from "../../components/PatientListOrderKit";
import { getStorage } from "../../hooks/use-local-storage";
import { closeModal, openModal, setShowPassedPatientsOnly } from "../../store";
import usePermissions from "../../hooks/use-permissions";
import { ViewPermissions } from "../../constants";
import { GetPhysicianKitPrice, GetPendingPatients } from "../../apollo-client/api-query";

import BaseButton from "../../components/BaseButton";
import { transformPendingPatient, transformActivePatient, paymentLabel } from "../../utils/patient-transforms";

const Patients = () => {
  const dispatch = useDispatch()
  const auth = useSelector(state => state.auth)
  const members = useSelector(state => state.members)
  const loadingAuth = useSelector(state => state.loadingAuth)
  const showSignUpSuccessModal = getStorage('showSignUpSuccessModal')

  const isShowPassedPatients = useSelector(state => state.showPassedPatientsOnly)
  const [searchKey, setSearchKey] = useState('')
  const [showReminder, setShowReminder] = useState(false);

  const {myPermissions} = usePermissions();

  // Users without VIEW_REPORT (e.g. Sales) can't see report summary counts on
  // the patient list, so the "Show completed patients only" filter — which is
  // based on reportSummary.inReviewCount / totalCount — would always match zero.
  // Hide the filter entirely for those users.
  const canFilterByReportCounts = myPermissions.includes(ViewPermissions.VIEW_REPORT);

  // Fetch physician's kit price to determine if "Add Patient" button should be shown
  const isPhysician = auth?.roles?.includes('PHYSICIAN');
  const { data: priceData } = useQuery(GetPhysicianKitPrice, {
    skip: !myPermissions.includes(ViewPermissions.ADD_PATIENT) || !isPhysician,
    fetchPolicy: 'network-only',
  });

  const hasValidKitPrice = priceData?.physicianKitPrice &&
                           priceData.physicianKitPrice.priceCents != null &&
                           priceData.physicianKitPrice.priceDollars != null &&
                           priceData.physicianKitPrice.active === true;

  // Fetch pending patients
  const { data: pendingPatientsData } = useQuery(GetPendingPatients, {
    variables: { first: 100 },
    fetchPolicy: 'cache-and-network',
  });

  // Transform pending patients to unified shape
  const pendingPatients = useMemo(() => {
    const edges = pendingPatientsData?.pendingPatients?.edges;
    if (!edges) return [];
    return edges
      .filter(({ node }) => node.status !== 'COMPLETED')
      .map(({ node }) => transformPendingPatient(node));
  }, [pendingPatientsData]);

  // Transform active patients to unified shape (deduplicated).
  // Sources from Redux `members` (populated from the `members` GraphQL query).
  // Deduplication: GLOBAL_VIEWER roles already return one row per patient, but
  // ASSIGNED_STAFF roles can have the same user in multiple user_staff rows,
  // so we dedup by the underlying user.id.
  const activePatients = useMemo(() => {
    if (!members || members.length === 0) return [];

    const seenIds = new Set();
    return members
      .filter(row => {
        const userId = row?.user?.id;
        if (!userId || seenIds.has(userId)) return false;
        seenIds.add(userId);
        return true;
      })
      .map(transformActivePatient);
  }, [members]);

  // Combined list: pending first, then active
  const combinedPatients = useMemo(() => {
    return [...pendingPatients, ...activePatients];
  }, [pendingPatients, activePatients]);

  // Filter: show passed → search
  const filteredPatients = useMemo(() => {
    let patients = combinedPatients;

    // 1. Apply "show completed patients" filter
    if (isShowPassedPatients) {
      patients = patients.filter(item =>
        item.patientType === 'active' &&
        item?.reportSummary?.inReviewCount === 0 && item?.reportSummary?.totalCount === 8
      );
    }

    // 2. Apply search filter
    if (!searchKey) return patients;

    return patients.filter(item =>
      item?.name?.toLocaleLowerCase()?.includes(searchKey) ||
      item?.email?.toLocaleLowerCase()?.includes(searchKey) ||
      item?.dateOfBirth?.includes(searchKey) ||
      item?.id?.toLocaleLowerCase()?.includes(searchKey) ||
      item?.phone?.includes(searchKey) ||
      item?.sex?.toLocaleLowerCase()?.includes(searchKey) ||
      item?.kitCode?.toLocaleLowerCase()?.includes(searchKey) ||
      (item?.patientType === 'active' ? 'paid'.includes(searchKey) : false) ||
      (item?.patientType === 'pending' ? paymentLabel(item?.status).includes(searchKey) : false) ||
      item?.createdAt?.includes(searchKey) ||
      item?.physicianName?.toLocaleLowerCase()?.includes(searchKey) ||
      item?.totalReportCounts?.toString() === searchKey ||
      item?.reviewCount?.toString() === searchKey ||
      item?.assignedStaff?.some(s => s?.staff?.name?.toLocaleLowerCase()?.includes(searchKey)) ||
      item?.assignedStaff?.some(s => s?.role?.toLocaleLowerCase()?.includes(searchKey))
    );
  }, [combinedPatients, searchKey, isShowPassedPatients]);

  useEffect(() => {
    if (showSignUpSuccessModal === 'show') {
      dispatch(
        openModal({
          currentModal: 'SIGN_UP_SUCCESS_MODAL',
      }))
    } else {
      dispatch(closeModal());
    }
  }, [dispatch, showSignUpSuccessModal])

  const handleChangeSearch = (e) => {
    const value = e.target.value.toLocaleLowerCase()
    setSearchKey(value)
    if (value) {
      const hasResults = combinedPatients.some(item =>
        item?.name?.toLocaleLowerCase()?.includes(value) ||
        item?.email?.toLocaleLowerCase()?.includes(value) ||
        item?.kitCode?.toLocaleLowerCase()?.includes(value)
      );
      setShowReminder(hasResults)
    } else {
      setShowReminder(false)
    }
  }

  const handleClickShowPassed = () => {
    dispatch(setShowPassedPatientsOnly(!isShowPassedPatients))
  }

  const handleRowClicked = useCallback((row) => {
    if (row?.patientType === 'pending') {
      dispatch(
        openModal({
          currentModal: 'PENDING_PATIENT_DETAIL_MODAL',
          modalProps: row,
        })
      );
    }
    // Active patients: only the "Reports" link navigates (no row-level click)
  }, [dispatch]);

  const handleOrderKitClicked = () => {
    dispatch(
      openModal({
          currentModal: 'OPEN_ORDER_KITS_MODAL',
          modalProps: {}
      })
    )
  }

  return (
    <MainContainer
      sx={{
        padding: { lg: '0 16px 50px' },
      }}
    >
      <BlockContainer
        sx={{
          position: 'relative',
          borderRadius: { md: '16px' },
          boxShadow: '0px 2px 10px 0px #5B87EF1A',
          bgcolor: 'white'
        }}
      >
        <Box
          sx={{
            position: 'relative',
            width: "100%",
            minHeight: "451px",
            padding: '32px 16px',
            bgcolor: 'white'
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              justifyContent: 'space-between',
              alignItems: { md: 'center' }
            }}
          >
            <Box sx={{
              display: 'flex',
              gap: '10px'
            }}>
              <Typography variant="h1">
                Patient List
              </Typography>
              {myPermissions.includes(ViewPermissions.ORDER_KIT) &&
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    cursor: 'pointer',
                  }}
                  onClick={handleOrderKitClicked}
                >
                  <Typography variant="body" color='primary.mainBlue'>
                    Need more kits? Click here
                  </Typography>
                </Box>
              }
            </Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                mt: { xs: '16px', md: 0 }
              }}
            >
              <Box sx={{ flex: 1 }}>
                <BaseIconInput
                  fullWidth
                  height='40'
                  placeholder='Search'
                  inputStyles={{
                    background: '#4266B833',
                    border: '#4266B833',
                    borderRadius: '40px',
                    color: '#46545F',
                    width: '100%'
                  }}
                  endIcon={<SearchIcon />}
                  value={searchKey}
                  onChange={handleChangeSearch}
                />
                {showReminder && (
                  <Box sx={{ ml: '8px' }}>
                    <Typography variant="body" color='primary.red'>
                      Scroll down to see search results
                    </Typography>
                  </Box>
                )}
              </Box>
              {myPermissions.includes(ViewPermissions.ADD_PATIENT) && hasValidKitPrice && (
                    <BaseButton
                      sx={{
                        display: 'inline-flex',
                        marginTop: '8px',
                        whiteSpace: 'nowrap',
                        minWidth: 'auto',
                        width: 'auto',
                        px: 3
                      }}
                      buttonType="primary"
                      onClick={() => {
                        dispatch(
                          openModal({
                            currentModal: 'ADD_PATIENT_MODAL',
                            modalProps: {}
                          })
                        )
                      }}
                    >
                      Add Patient
                    </BaseButton>
              )}
            </Box>
          </Box>

          {/* Filter controls + patient count */}
          <Box sx={{ mt: '20px', width: '100%' }}>
            {canFilterByReportCounts && (isShowPassedPatients || combinedPatients.length > 5) && (
            <Box
              sx={{
                display: loadingAuth ? 'none' : 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: '8px',
                maxWidth: '100%',
                mb: 1
              }}
            >
              <Typography
                variant="body"
                color='primary.mainBlue'
                sx={{ cursor: 'pointer' }}
                onClick={handleClickShowPassed}
              >
                {isShowPassedPatients ? 'Show all patients' : 'Show completed patients only'}
              </Typography>

            </Box>
            )}

            {/* Desktop table */}
            <PatientTable rowData={filteredPatients} onRowClicked={handleRowClicked} />

            {/* Mobile list */}
            <PatientList data={filteredPatients} onRowClicked={handleRowClicked} />
          </Box>

          {/* Order Kits */}
          {myPermissions.includes(ViewPermissions.ORDER_KIT) &&
            <Box sx={{
              marginTop: '40px'
            }}>
              <PatientListOrderKit/>
            </Box>
          }
        </Box>
      </BlockContainer>
    </MainContainer>
  );
};

export default Patients;
